import type { LocalDate } from '../dates';
import type { LedgerEntryLike } from '../ledger/counting';
import type { Mutation } from '../mutations';
import type { BudgetSettings } from '../rules/readModel';
import { MAX_AMOUNT_MINOR } from '../spending/expense';
import type { BuildCtx } from '../spending/types';
import { isValidLocalDate } from './schedule';
import { budgetMutation, type BudgetResult } from './types';

/**
 * v0.5 Accounts: the real places money sits (Cash, GCash, a bank). Separate from funds, which are
 * spending envelopes. A balance is never stored: it is the opening balance plus every ledger
 * movement on the account dated from the opening date up to the day asked about.
 *
 * How each ledger row moves an account (the row's accountId / toAccountId):
 * - expense: out of accountId;  refund and income: into accountId;
 * - transfer: out of accountId (if set) and into toAccountId (if set). A transfer between two
 *   accounts has both; a card statement payment only accountId (the card isn't an account); a
 *   deposit of expected money or an upward adjustment only toAccountId; a downward adjustment only
 *   accountId. Transfers are never spending or income (ledger/counting.ts), so none of these
 *   touches Spending, income, what's left or safe to spend.
 */

export type AccountType = 'cash' | 'ewallet' | 'bank';
export const ACCOUNT_TYPES: readonly AccountType[] = ['cash', 'ewallet', 'bank'];

/** v0.6: what the account is for. Savings and investments start savings-only. */
export type AccountKind = 'spending' | 'savings' | 'investment';
export const ACCOUNT_KINDS: readonly AccountKind[] = ['spending', 'savings', 'investment'];

/** v0.6: how interest is credited: every month, every year, or once at maturity (time deposits). */
export type AccountGrowth = 'monthly' | 'yearly' | 'maturity';
export const ACCOUNT_GROWTHS: readonly AccountGrowth[] = ['monthly', 'yearly', 'maturity'];

/** The highest yearly rate accepted, in basis points (100%). */
export const MAX_RATE_BP = 10_000;

export interface AccountLike {
  id: string;
  name: string;
  type: AccountType | null;
  /** What the account held on openingDate, in centavos. */
  openingBalanceMinor: number;
  openingDate: LocalDate;
  position: number;
  deletedAt: string | null;
  /** v0.6 (absent on older rows and fixtures = a spending account used for spending). */
  kind?: AccountKind;
  /** v0.6 "Use for spending": in From chips and Safe to spend. Absent = on. */
  useForSpending?: boolean;
  /** Yearly interest in basis points (300 = 3%); null = none. */
  annualRateBp?: number | null;
  growth?: AccountGrowth | null;
  /** The day interest starts (null = openingDate). */
  startDate?: LocalDate | null;
  maturityDate?: LocalDate | null;
  /** Can't be taken out before maturity. */
  locked?: boolean;
}

/** v0.6: is the account money you spend from (From chips, Safe to spend, "Your money")? */
export function usedForSpending(a: Pick<AccountLike, 'useForSpending'>): boolean {
  return a.useForSpending !== false;
}

/** The "Use for spending" default for a new account of this kind (spending on, savings off). */
export function defaultUseForSpending(kind: AccountKind): boolean {
  return kind === 'spending';
}

/** A ledger row as balances read it. */
export type AccountEntryLike = Pick<
  LedgerEntryLike,
  'id' | 'date' | 'amountMinor' | 'kind' | 'sourceModule' | 'note' | 'deletedAt'
> & {
  accountId: string | null;
  toAccountId: string | null;
  sourceId: string | null;
};

export const MAX_ACCOUNT_NAME = 40;
export const TRANSFER_FEE_NOTE = 'Transfer fee';
export const ADJUSTMENT_NOTE = 'Balance adjustment';

/** What kind of money movement a ledger row is, as Accounts and history name it. */
export type MovementKind =
  | 'expense'
  | 'refund'
  | 'income'
  | 'transfer'
  | 'card_payment'
  | 'deposit'
  | 'interest'
  | 'adjustment';

/**
 * v0.6 "Interest received": a deposit into the account whose sourceId is the account itself.
 * A transfer-kind row, so it is never income (like Coming in).
 */
export function isInterestEntry(
  e: Pick<AccountEntryLike, 'kind' | 'sourceModule' | 'sourceId' | 'accountId' | 'toAccountId'>,
): boolean {
  return (
    e.kind === 'transfer' &&
    e.sourceModule === 'budget' &&
    e.accountId === null &&
    e.toAccountId !== null &&
    e.sourceId === e.toAccountId
  );
}

export function movementKind(
  e: Pick<AccountEntryLike, 'kind' | 'sourceModule' | 'sourceId' | 'accountId' | 'toAccountId'>,
): MovementKind {
  if (e.kind !== 'transfer') return e.kind;
  if (e.sourceModule === 'payments') return 'card_payment';
  if (e.accountId && e.toAccountId) return 'transfer';
  if (isInterestEntry(e)) return 'interest';
  if (e.sourceModule === 'budget' && e.sourceId) return 'deposit';
  return 'adjustment';
}

/** A transfer fee: the expense row a transfer wrote next to itself (sourceId = the transfer). */
export function isTransferFee(
  e: Pick<AccountEntryLike, 'kind' | 'sourceModule' | 'sourceId' | 'note'>,
): boolean {
  return (
    e.kind === 'expense' &&
    e.sourceModule === 'budget' &&
    e.sourceId !== null &&
    e.note === TRANSFER_FEE_NOTE
  );
}

/** How much a row adds to (positive) or takes from (negative) one account. */
export function accountEffect(e: AccountEntryLike, accountId: string): number {
  if (e.deletedAt !== null) return 0;
  switch (e.kind) {
    case 'expense':
      return e.accountId === accountId ? -e.amountMinor : 0;
    case 'refund':
    case 'income':
      return e.accountId === accountId ? e.amountMinor : 0;
    case 'transfer':
      return (
        (e.toAccountId === accountId ? e.amountMinor : 0) -
        (e.accountId === accountId ? e.amountMinor : 0)
      );
  }
}

/** Rows that move the account, from its opening date through `asOf` (old rows with no account never do). */
export function accountMovements<T extends AccountEntryLike>(
  account: Pick<AccountLike, 'id' | 'openingDate'>,
  entries: readonly T[],
  asOf: LocalDate,
): T[] {
  return entries.filter(
    (e) =>
      e.deletedAt === null &&
      (e.accountId === account.id || e.toAccountId === account.id) &&
      e.date >= account.openingDate &&
      e.date <= asOf,
  );
}

/** Balance = opening balance + income in − spending out − payments out ± transfers (derived). */
export function accountBalance(
  account: Pick<AccountLike, 'id' | 'openingDate' | 'openingBalanceMinor'>,
  entries: readonly AccountEntryLike[],
  asOf: LocalDate,
): number {
  return accountMovements(account, entries, asOf).reduce(
    (sum, e) => sum + accountEffect(e, account.id),
    account.openingBalanceMinor,
  );
}

export interface AccountStatus {
  account: AccountLike;
  balanceMinor: number;
}

export function sortAccounts<T extends Pick<AccountLike, 'position' | 'name'>>(
  accounts: readonly T[],
): T[] {
  return [...accounts].sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
}

/** Every live account with its balance, in the user's order. */
export function accountStatuses(
  accounts: readonly AccountLike[],
  entries: readonly AccountEntryLike[],
  asOf: LocalDate,
): AccountStatus[] {
  return sortAccounts(accounts.filter((a) => a.deletedAt === null)).map((account) => ({
    account,
    balanceMinor: accountBalance(account, entries, asOf),
  }));
}

/** "Money on hand": every live account's balance added up. */
export function moneyOnHand(statuses: readonly AccountStatus[]): number {
  return statuses.reduce((s, a) => s + a.balanceMinor, 0);
}

/** v0.6: split into money you spend from and savings-only accounts (each keeps the user's order). */
export function splitBySpending(statuses: readonly AccountStatus[]): {
  spending: AccountStatus[];
  savings: AccountStatus[];
} {
  return {
    spending: statuses.filter((a) => usedForSpending(a.account)),
    savings: statuses.filter((a) => !usedForSpending(a.account)),
  };
}

/** v0.6: a locked account (a time deposit) can't be taken out of before its maturity date. */
export function lockedOn(
  a: Pick<AccountLike, 'locked' | 'maturityDate'>,
  today: LocalDate,
): boolean {
  return a.locked === true && (a.maturityDate == null || today < a.maturityDate);
}

/**
 * v0.6: accounts offered as "From" (used for spending, and not locked on `today` when given), plus
 * the one already picked so an edit keeps it.
 */
export function spendingAccounts<
  T extends Pick<AccountLike, 'id' | 'useForSpending' | 'locked' | 'maturityDate'>,
>(accounts: readonly T[], keepId?: string | null, today?: LocalDate): T[] {
  return accounts.filter(
    (a) =>
      (keepId != null && a.id === keepId) ||
      (usedForSpending(a) && (today === undefined || !lockedOn(a, today))),
  );
}

/** v0.6: accounts money can be moved out of (a transfer's source): not locked on `today`. */
export function transferSources<T extends Pick<AccountLike, 'id' | 'locked' | 'maturityDate'>>(
  accounts: readonly T[],
  today: LocalDate,
  keepId?: string | null,
): T[] {
  return accounts.filter((a) => (keepId != null && a.id === keepId) || !lockedOn(a, today));
}

// ---------- Which account a movement uses ----------

/** Only a live account id survives; a deleted or unknown one reads as none. */
function live(
  id: string | null | undefined,
  accounts?: readonly Pick<AccountLike, 'id' | 'deletedAt'>[],
) {
  if (!id) return null;
  if (!accounts) return id;
  return accounts.some((a) => a.id === id && a.deletedAt === null) ? id : null;
}

/** A live account that is used for spending (a default "From" never lands in savings-only). */
function spendable(
  id: string | null | undefined,
  accounts?: readonly Pick<AccountLike, 'id' | 'deletedAt' | 'useForSpending'>[],
) {
  if (!id) return null;
  if (!accounts) return id;
  return accounts.some((a) => a.id === id && a.deletedAt === null && usedForSpending(a))
    ? id
    : null;
}

/**
 * The account spending comes out of (Ivan: "Yes, with a default"): the one picked on the entry,
 * else the fund's default account (its first, only while funds are in use), else Budget's "Pay
 * from" default, else none. Accounts belong to Budget, so nothing is picked while it isn't usable.
 * v0.6: a default never falls on a savings-only account (only a picked one may).
 */
export function payFromAccount(
  picked: string | null | undefined,
  fund: { accountIds: readonly string[] } | null | undefined,
  settings: Pick<BudgetSettings, 'useFunds' | 'defaultAccountId'>,
  budgetUsable: boolean,
  accounts?: readonly Pick<AccountLike, 'id' | 'deletedAt' | 'useForSpending'>[],
): string | null {
  if (!budgetUsable) return null;
  if (picked) return live(picked, accounts);
  const fromFund =
    settings.useFunds !== false && fund
      ? fund.accountIds.map((id) => spendable(id, accounts)).find(Boolean)
      : null;
  return fromFund ?? spendable(settings.defaultAccountId, accounts);
}

/** The account income lands in: picked, else the income source's own, else Budget's "Receive into" default. */
export function receiveIntoAccount(
  picked: string | null | undefined,
  source: { accountId?: string | null } | null | undefined,
  settings: Pick<BudgetSettings, 'incomeAccountId'>,
  budgetUsable: boolean,
  accounts?: readonly Pick<AccountLike, 'id' | 'deletedAt'>[],
): string | null {
  if (!budgetUsable) return null;
  if (picked) return live(picked, accounts);
  return live(source?.accountId, accounts) ?? live(settings.incomeAccountId, accounts);
}

/**
 * The account chips to offer for an expense, in order: the fund's accounts first (its default
 * account leading) while funds are in use, then the rest in the user's order.
 */
export function orderAccountsForFund<T extends Pick<AccountLike, 'id'>>(
  accounts: readonly T[],
  fund: { accountIds: readonly string[] } | null | undefined,
  useFunds: boolean,
): T[] {
  if (!fund || !useFunds || fund.accountIds.length === 0) return [...accounts];
  const first = fund.accountIds
    .map((id) => accounts.find((a) => a.id === id))
    .filter((a): a is T => a !== undefined);
  return [...first, ...accounts.filter((a) => !fund.accountIds.includes(a.id))];
}

// ---------- Builders ----------

export interface AccountInput {
  name: string;
  type: AccountType | null;
  /** "Balance now", in centavos (may be negative: an overdrawn account). */
  openingBalanceMinor: number;
  /** The day that balance is true for. */
  openingDate: LocalDate;
  /** v0.6 savings fields; absent = a spending account with no interest. */
  kind?: AccountKind;
  /** Absent = the kind's default (defaultUseForSpending). */
  useForSpending?: boolean;
  annualRateBp?: number | null;
  growth?: AccountGrowth | null;
  startDate?: LocalDate | null;
  maturityDate?: LocalDate | null;
  locked?: boolean;
}

export type AccountError =
  | 'name_required'
  | 'name_too_long'
  | 'name_taken'
  | 'bad_type'
  | 'bad_amount'
  | 'bad_date'
  | 'bad_rate'
  | 'bad_maturity';

const validMinor = (n: number) => Number.isInteger(n) && Math.abs(n) <= MAX_AMOUNT_MINOR;
const validPositive = (n: number) => Number.isInteger(n) && n > 0 && n <= MAX_AMOUNT_MINOR;

function validateAccount(
  input: AccountInput,
  others: readonly Pick<AccountLike, 'name' | 'deletedAt'>[],
): AccountError | null {
  const name = input.name.trim();
  if (name === '') return 'name_required';
  if (name.length > MAX_ACCOUNT_NAME) return 'name_too_long';
  if (
    others.some((a) => a.deletedAt === null && a.name.trim().toLowerCase() === name.toLowerCase())
  )
    return 'name_taken';
  if (input.type !== null && !ACCOUNT_TYPES.includes(input.type)) return 'bad_type';
  if (!validMinor(input.openingBalanceMinor)) return 'bad_amount';
  if (!isValidLocalDate(input.openingDate)) return 'bad_date';
  if (input.kind !== undefined && !ACCOUNT_KINDS.includes(input.kind)) return 'bad_type';
  const rate = input.annualRateBp ?? null;
  if (rate !== null && (!Number.isInteger(rate) || rate <= 0 || rate > MAX_RATE_BP))
    return 'bad_rate';
  if (input.growth != null && !ACCOUNT_GROWTHS.includes(input.growth)) return 'bad_rate';
  if (input.startDate != null && !isValidLocalDate(input.startDate)) return 'bad_date';
  if (input.maturityDate != null) {
    if (!isValidLocalDate(input.maturityDate)) return 'bad_maturity';
    if (input.maturityDate <= (input.startDate ?? input.openingDate)) return 'bad_maturity';
  }
  if (rate !== null && input.growth === 'maturity' && input.maturityDate == null)
    return 'bad_maturity';
  return null;
}

/** Every field, savings ones included (derived defaults are written in the insert, never later). */
function accountValues(input: AccountInput): Record<string, unknown> {
  const kind = input.kind ?? 'spending';
  const rate = input.annualRateBp ?? null;
  return {
    name: input.name.trim().replace(/\s+/g, ' '),
    type: input.type,
    openingBalanceMinor: input.openingBalanceMinor,
    openingDate: input.openingDate,
    kind,
    useForSpending: input.useForSpending ?? defaultUseForSpending(kind),
    annualRateBp: rate,
    // With a rate and no choice, interest is credited monthly (a savings account's usual).
    growth: rate === null ? null : (input.growth ?? 'monthly'),
    startDate: input.startDate ?? null,
    maturityDate: input.maturityDate ?? null,
    // "Locked until maturity" needs a maturity date.
    locked: input.maturityDate != null && input.locked === true,
  };
}

/** The stored fields under accountValues' names and defaults, for comparing on edit. */
function storedValues(a: AccountLike): Record<string, unknown> {
  return {
    name: a.name,
    type: a.type,
    openingBalanceMinor: a.openingBalanceMinor,
    openingDate: a.openingDate,
    kind: a.kind ?? 'spending',
    useForSpending: usedForSpending(a),
    annualRateBp: a.annualRateBp ?? null,
    growth: a.growth ?? null,
    startDate: a.startDate ?? null,
    maturityDate: a.maturityDate ?? null,
    locked: a.locked ?? false,
  };
}

/** Adding an account with "Balance now": the opening balance lives on the account, never as income. */
export function buildAddAccount(
  input: AccountInput,
  existing: readonly AccountLike[],
  ctx: BuildCtx,
): BudgetResult<{ accountId: string; mutation: Mutation }, AccountError> {
  const error = validateAccount(input, existing);
  if (error) return { ok: false, error };
  const accountId = ctx.newId();
  const position = existing.reduce((max, a) => Math.max(max, a.position), 0) + 1;
  return {
    ok: true,
    accountId,
    mutation: budgetMutation(ctx, 'money_accounts', 'insert', accountId, {
      ...accountValues(input),
      currency: 'PHP',
      position,
    }),
  };
}

/** Writes only the fields that changed. */
export function buildEditAccount(
  before: AccountLike,
  input: AccountInput,
  existing: readonly AccountLike[],
  ctx: BuildCtx,
): BudgetResult<{ mutation: Mutation | null }, AccountError> {
  const error = validateAccount(
    input,
    existing.filter((a) => a.id !== before.id),
  );
  if (error) return { ok: false, error };
  const next = accountValues(input);
  const stored = storedValues(before);
  const values: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(next)) {
    if (stored[k] !== v) values[k] = v;
  }
  if (Object.keys(values).length === 0) return { ok: true, mutation: null };
  return {
    ok: true,
    mutation: budgetMutation(ctx, 'money_accounts', 'update', before.id, values),
  };
}

/** Soft delete: past entries keep their accountId (History still names it). */
export function buildDeleteAccount(id: string, ctx: BuildCtx): Mutation {
  return budgetMutation(ctx, 'money_accounts', 'delete', id);
}

/** Undo of a delete: a plain insert on the soft-deleted id (conventions §14). */
export function buildRestoreAccount(id: string, ctx: BuildCtx): Mutation {
  return budgetMutation(ctx, 'money_accounts', 'insert', id, {});
}

export interface TransferInput {
  fromAccountId: string;
  toAccountId: string;
  amountMinor: number;
  /** Optional fee, taken from the "from" account as its own expense (it is real spending). */
  feeMinor?: number | null;
  date: LocalDate;
  note?: string | null;
}

export type TransferError =
  'same_account' | 'not_found' | 'bad_amount' | 'bad_fee' | 'bad_date' | 'note_too_long';

export const MAX_TRANSFER_NOTE = 200;

function ledgerValues(v: {
  date: LocalDate;
  amountMinor: number;
  kind: 'transfer' | 'expense';
  accountId: string | null;
  toAccountId: string | null;
  sourceId: string | null;
  note: string | null;
  tz: string;
}): Record<string, unknown> {
  return {
    date: v.date,
    amountMinor: v.amountMinor,
    currency: 'PHP',
    kind: v.kind,
    categoryId: null,
    method: null,
    fundId: null,
    accountId: v.accountId,
    toAccountId: v.toAccountId,
    sourceModule: 'budget',
    sourceId: v.sourceId,
    note: v.note,
    recordedTz: v.tz,
  };
}

/**
 * Move money between two accounts (GoTyme → GCash ₱1,000): one ledger row of kind transfer with
 * both accounts, never spending or income. A fee is a separate expense from the "from" account,
 * linked by sourceId, so undoing the transfer takes the fee with it.
 */
export function buildTransfer(
  input: TransferInput,
  accounts: readonly AccountLike[],
  ctx: BuildCtx,
): BudgetResult<
  { transferId: string; feeId: string | null; mutations: Mutation[] },
  TransferError
> {
  if (input.fromAccountId === input.toAccountId) return { ok: false, error: 'same_account' };
  const liveIds = new Set(accounts.filter((a) => a.deletedAt === null).map((a) => a.id));
  if (!liveIds.has(input.fromAccountId) || !liveIds.has(input.toAccountId))
    return { ok: false, error: 'not_found' };
  if (!validPositive(input.amountMinor)) return { ok: false, error: 'bad_amount' };
  const fee = input.feeMinor ?? 0;
  if (fee !== 0 && !validPositive(fee)) return { ok: false, error: 'bad_fee' };
  if (!isValidLocalDate(input.date)) return { ok: false, error: 'bad_date' };
  const note = (input.note ?? '').trim().replace(/\s+/g, ' ');
  if (note.length > MAX_TRANSFER_NOTE) return { ok: false, error: 'note_too_long' };
  const transferId = ctx.newId();
  const mutations = [
    budgetMutation(
      ctx,
      'ledger_entries',
      'insert',
      transferId,
      ledgerValues({
        date: input.date,
        amountMinor: input.amountMinor,
        kind: 'transfer',
        accountId: input.fromAccountId,
        toAccountId: input.toAccountId,
        sourceId: null,
        note: note === '' ? null : note,
        tz: ctx.tz,
      }),
    ),
  ];
  let feeId: string | null = null;
  if (fee > 0) {
    feeId = ctx.newId();
    mutations.push(
      budgetMutation(
        ctx,
        'ledger_entries',
        'insert',
        feeId,
        ledgerValues({
          date: input.date,
          amountMinor: fee,
          kind: 'expense',
          accountId: input.fromAccountId,
          toAccountId: null,
          sourceId: transferId,
          note: TRANSFER_FEE_NOTE,
          tz: ctx.tz,
        }),
      ),
    );
  }
  return { ok: true, transferId, feeId, mutations };
}

/** Undo (or delete) a transfer, or an adjustment: the row and its fee are soft-deleted (History keeps them). */
export function buildUndoTransfer(
  transferId: string,
  feeIds: readonly string[],
  ctx: BuildCtx,
): Mutation[] {
  return [transferId, ...feeIds].map((id) => budgetMutation(ctx, 'ledger_entries', 'delete', id));
}

/** Undo of buildUndoTransfer: plain inserts on the deleted ids bring them back. */
export function buildRestoreTransfer(
  transferId: string,
  feeIds: readonly string[],
  ctx: BuildCtx,
): Mutation[] {
  return [transferId, ...feeIds].map((id) =>
    budgetMutation(ctx, 'ledger_entries', 'insert', id, {}),
  );
}

/**
 * "Adjust balance": the user types the real balance and the difference is recorded as an
 * adjustment (a one-sided transfer, never spending or income), visible in history. Null when the
 * balance already matches.
 */
export function buildAdjustBalance(
  account: Pick<AccountLike, 'id' | 'deletedAt'>,
  currentMinor: number,
  realMinor: number,
  date: LocalDate,
  ctx: BuildCtx,
): BudgetResult<
  { entryId: string | null; mutation: Mutation | null },
  'bad_amount' | 'bad_date' | 'not_found'
> {
  if (account.deletedAt !== null) return { ok: false, error: 'not_found' };
  if (!validMinor(realMinor)) return { ok: false, error: 'bad_amount' };
  if (!isValidLocalDate(date)) return { ok: false, error: 'bad_date' };
  const delta = realMinor - currentMinor;
  if (delta === 0) return { ok: true, entryId: null, mutation: null };
  if (!validPositive(Math.abs(delta))) return { ok: false, error: 'bad_amount' };
  const entryId = ctx.newId();
  return {
    ok: true,
    entryId,
    mutation: budgetMutation(
      ctx,
      'ledger_entries',
      'insert',
      entryId,
      ledgerValues({
        date,
        amountMinor: Math.abs(delta),
        kind: 'transfer',
        accountId: delta < 0 ? account.id : null,
        toAccountId: delta > 0 ? account.id : null,
        sourceId: null,
        note: ADJUSTMENT_NOTE,
        tz: ctx.tz,
      }),
    ),
  };
}
